import { useMemo, useState } from 'react'
import usePersistedFilters from '../../hooks/usePersistedFilters'
import useFetchData from '../../hooks/UseFetchData'
import Table from '../../components/his/invoices/Table'
import PageHeader from '../../shared/PageHeader'
import {
  FaArrowCircleLeft,
  FaArrowCircleRight,
  FaFileExcel,
  FaFilePdf
} from 'react-icons/fa'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'
import moment from 'moment'
import { useSelector } from 'react-redux'
import { ColumnGroup } from 'primereact/columngroup'
import { Row as ColumnRow } from 'primereact/row'

import CustomButton from '../../shared/CustomButton'
import DropdownField from '../../shared/DropdownField'
import XLSX from 'xlsx-js-style'
import { formatDateTime } from '../../utils/FormateDateTime'
import Filters from '../../components/his/invoices/Filters'
import { Column } from 'primereact/column'
import { AmiriRegBase64 } from '../../assets/fonts/Amiri'
import { AmiriBoldBase64 } from '../../assets/fonts/AmiriB'
import {
  REPORT_MODES,
  reportModeOptions,
  splitSectionsByUser,
} from '../../utils/UserCollectionReportModes'

const toNumber = value => Number.parseFloat(value || 0)
const toMoney = value => toNumber(value).toFixed(2)

const totalFields = [
  'patient_share',
  'patient_vat',
  'net_patient_payable',
  'company_share',
  'company_vat',
  'net_company_payable',
  'discount',
  'vat',
  'gross',
  'net_with_vat',
  'net_without_vat',
  'total_net',
  'credit_remaining'
]

const getUserName = row => {
  if (
    row?.name &&
    row?.created_by &&
    String(row.name) === String(row.created_by)
  )
    return row.name
  return row?.name || row?.created_by || 'Unknown User'
}

const getUserKey = row => row?.created_by || row?.name || 'unknown'

const sumDetailedRows = rows =>
  rows.reduce(
    (total, row) => {
      totalFields.forEach(field => {
        total[field] += toNumber(row?.[field])
      })
      return total
    },
    totalFields.reduce((acc, field) => ({ ...acc, [field]: 0 }), {})
  )

const formatDetailedRow = row => {
  const patientShare = toNumber(row?.patient_share)
  const patientVat = toNumber(row?.patient_vat)
  const patientTotal = toNumber(row?.net_patient_payable)
  const companyShare = toNumber(row?.company_share)
  const companyVat = toNumber(row?.company_vat)
  const companyTotal = toNumber(row?.net_company_payable)

  return {
    ...row,
    invoice_number: row?.cash_invoice_number,
    total_vat: toMoney(row?.vat),
    total_gross_amount: toMoney(row?.gross),
    total_discount: toMoney(row?.discount),
    total_amount_w_vat: toMoney(row?.net_with_vat),
    total_amount_wo_vat: toMoney(row?.net_without_vat),
    total_net_amount: toMoney(row?.total_net),
    credit_remaining_amount: toMoney(row?.credit_remaining),
    patient_name: row?.patient_name || row?.patient?.full_name || '-',
    group: {
      patient: {
        share: toMoney(patientShare),
        vat: toMoney(patientVat),
        total: toMoney(patientTotal || patientShare + patientVat)
      },
      company: {
        name: row?.company?.name || '-',
        share: toMoney(companyShare),
        vat: toMoney(companyVat),
        total: toMoney(companyTotal || companyShare + companyVat)
      }
    }
  }
}

const formatDetailedTotal = total => ({
  separator: true,
  patient_name: total?.name || 'Subtotal',
  total_vat: toMoney(total?.vat),
  total_gross_amount: toMoney(total?.gross),
  total_discount: toMoney(total?.discount),
  total_amount_w_vat: toMoney(total?.net_with_vat),
  total_amount_wo_vat: toMoney(total?.net_without_vat),
  total_net_amount: toMoney(total?.total_net),
  credit_remaining_amount: toMoney(total?.credit_remaining),
  group: {
    patient: {
      share: toMoney(total?.patient_share),
      vat: toMoney(total?.patient_vat),
      total: toMoney(total?.net_patient_payable)
    },
    company: {
      name: '',
      share: toMoney(total?.company_share),
      vat: toMoney(total?.company_vat),
      total: toMoney(total?.net_company_payable)
    }
  }
})

const flattenGroupedDetailedReports = data => {
  const result = []
  const pdf = []

  ;(data || []).forEach(section => {
    const sectionHeader = {
      headerLabel: 'Group',
      headerValue: section?.group_name,
      isGroupHeader: true,
      isSectionHeader: true
    }
    const sectionSubtotal = {
      ...formatDetailedTotal(section?.total),
      patient_name: `${section?.group_name} Total`,
      isSectionTotal: true
    }

    result.push(sectionHeader)

    const userGroups = (section?.rows || []).reduce((groups, row) => {
      const key = getUserKey(row)
      if (!groups[key]) {
        groups[key] = {
          label: getUserName(row),
          rows: []
        }
      }
      groups[key].rows.push(row)
      return groups
    }, {})

    Object.values(userGroups).forEach(group => {
      const rows = group.rows.map(formatDetailedRow)
      const userSubtotal = {
        ...formatDetailedTotal({
          ...sumDetailedRows(group.rows),
          name: `${group.label} Total`
        }),
        isUserTotal: true
      }

      result.push({
        headerLabel: 'User Name',
        headerValue: group.label,
        isGroupHeader: true,
        isUserHeader: true
      })
      result.push(...rows)
      result.push(userSubtotal)

      pdf.push({
        header: { key: `${section?.group_name} / User`, value: group.label },
        sectionName: section?.group_name,
        children: rows,
        footer: {
          key: `${group.label} Total`,
          value: userSubtotal
        }
      })
    })

    result.push(sectionSubtotal)
    pdf.push({
      header: { key: 'Group', value: section?.group_name },
      isSectionTotal: true,
      children: [],
      footer: {
        key: `${section?.group_name} Total`,
        value: sectionSubtotal
      }
    })
  })

  return { result, pdf }
}

const flattenUserDetailedReport = (section, user) => {
  const rows = user.rows.map(formatDetailedRow)
  const userSubtotal = {
    ...formatDetailedTotal({
      ...sumDetailedRows(user.rows),
      name: 'Total'
    }),
    isUserTotal: true
  }

  return {
    result: [...rows, userSubtotal],
    pdf: [
      {
        header: { key: `${section?.group_name} / User`, value: user.label },
        sectionName: section?.group_name,
        children: rows,
        footer: {
          key: 'Total',
          value: userSubtotal
        }
      }
    ]
  }
}

const detailedCellStyle = row => {
  if (row?.isSectionHeader) {
    return {
      backgroundColor: '#D1D5DB',
      color: '#111827',
      fontWeight: 'bold',
      borderTop: '2px solid #9CA3AF',
      borderBottom: '2px solid #9CA3AF'
    }
  }
  if (row?.isUserHeader) {
    return {
      backgroundColor: '#E5E7EB',
      color: '#111827',
      fontWeight: 'bold'
    }
  }
  if (row?.isSectionTotal) {
    return {
      backgroundColor: '#4B5563',
      color: '#FFFFFF',
      fontWeight: 'bold'
    }
  }
  if (row?.separator) {
    return {
      backgroundColor: '#E5E7EB',
      color: '#111827',
      fontWeight: 'bold'
    }
  }
  return {
    backgroundColor: '#F3F4F6'
  }
}

const detailedRowClassName = row => {
  if (row?.isSectionHeader) return 'user-collection-detail-section-row'
  if (row?.isSectionTotal) return 'user-collection-detail-section-total-row'
  if (row?.separator) return 'user-collection-detail-subtotal-row'
  if (row?.isUserHeader) return 'user-collection-detail-user-row'
  return 'user-collection-detail-data-row'
}

const columns = t =>
  [
    {
      header: t('Date & Time'),
      bodyCellClass: 'border !border-gray-300',
      body: row =>
        row?.isGroupHeader ? (
          <span className='font-bold text-[14px] uppercase'>
            {t(row?.headerLabel)}
          </span>
        ) : row?.separator ? (
          <span className='font-bold text-[12px]'>{t('Subtotal')}</span>
        ) : (
          row?.created_at && formatDateTime(row?.created_at)
        ),
      bodyCellStyle: detailedCellStyle
    },
    {
      header: t('Invoice No'),
      bodyCellClass: 'border !border-gray-300',
      body: row =>
        row?.isGroupHeader ? (
          <span className='font-bold text-[14px] uppercase'>
            {row?.headerValue}
          </span>
        ) : row?.separator ? (
          <span className='font-bold text-[12px]'></span>
        ) : (
          row?.cash_invoice_number || '-'
        ),
      headerCellClass: '!bg-gray-200',
      bodyCellStyle: detailedCellStyle
    },
    {
      header: t('MRN'),
      bodyCellClass: 'border !border-gray-300',
      body: row =>
        row?.isGroupHeader ? (
          <span className='font-bold text-[14px] uppercase'></span>
        ) : row?.separator ? (
          <span className='font-bold text-[12px]'></span>
        ) : (
          row?.patient?.file_no || '-'
        ),
      headerCellClass: '!bg-gray-200'
    },
    {
      header: t('Patient Name'),
      bodyCellClass: 'border !border-gray-300',
      body: row =>
        row?.isGroupHeader ? (
          <span className='font-bold text-[14px] uppercase'></span>
        ) : row?.separator ? (
          <span className='font-bold text-[12px]'>{row?.patient_name}</span>
        ) : (
          row?.patient_name
        ),
      headerCellClass: '!bg-gray-200'
    },
    {
      header: t('Patient Share'),
      bodyCellClass: 'border !border-gray-300',
      body: row =>
        row?.isGroupHeader ? (
          <span className='font-bold text-[14px] uppercase'></span>
        ) : row?.separator ? (
          <span className='font-bold text-[12px] '>
            {row?.group?.patient?.share}
          </span>
        ) : (
          row?.group?.patient?.share
        ),
      headerCellClass: '!bg-gray-200'
    },
    {
      header: t('Patient VAT'),
      bodyCellClass: 'border !border-gray-300',
      body: row =>
        row?.isGroupHeader ? (
          <span className='font-bold text-[14px] uppercase'></span>
        ) : row?.separator ? (
          <span className='font-bold text-[12px]'>
            {row?.group?.patient?.vat}
          </span>
        ) : (
          row?.group?.patient?.vat
        ),
      headerCellClass: '!bg-gray-200'
    },
    {
      header: t('Patient Total'),
      bodyCellClass: 'border !border-gray-300',
      body: row =>
        row?.isGroupHeader ? (
          <span className='font-bold text-[14px] uppercase'></span>
        ) : row?.separator ? (
          <span className='font-bold text-[12px]'>
            {row?.group?.patient?.total}
          </span>
        ) : (
          row?.group?.patient?.total
        ),
      headerCellClass: '!bg-gray-200'
    },
    {
      header: t('Company Name'),
      bodyCellClass: 'border !border-gray-300',
      body: row =>
        row?.isGroupHeader ? (
          <span className='font-bold text-[14px] uppercase'></span>
        ) : row?.separator ? (
          <span className='font-bold text-[12px]'>{row?.company?.name}</span>
        ) : (
          row?.company?.name
        ),
      headerCellClass: '!bg-gray-200'
    },
    {
      header: t('Company Share'),
      bodyCellClass: 'border !border-gray-300',
      body: row =>
        row?.isGroupHeader ? (
          <span className='font-bold text-[14px] uppercase'></span>
        ) : row?.separator ? (
          <span className='font-bold text-[12px]'>
            {row?.group?.company?.share}
          </span>
        ) : (
          row?.group?.company?.share
        ),
      headerCellClass: '!bg-gray-200'
    },
    {
      header: t('Company VAT'),
      bodyCellClass: 'border !border-gray-300',
      body: row =>
        row?.isGroupHeader ? (
          <span className='font-bold text-[14px] uppercase'></span>
        ) : row?.separator ? (
          <span className='font-bold text-[12px]'>
            {row?.group?.company?.vat}
          </span>
        ) : (
          row?.group?.company?.vat
        ),
      headerCellClass: '!bg-gray-200'
    },
    {
      header: t('Company Total'),
      bodyCellClass: 'border !border-gray-300',
      body: row =>
        row?.isGroupHeader ? (
          <span className='font-bold text-[14px] uppercase'></span>
        ) : row?.separator ? (
          <span className='font-bold text-[12px]'>
            {row?.group?.company?.total}
          </span>
        ) : (
          row?.group?.company?.total
        ),
      headerCellClass: '!bg-gray-200'
    },
    {
      header: t('Gross'),
      bodyCellClass: 'border !border-gray-300',
      body: row =>
        row?.isGroupHeader ? (
          <span className='font-bold text-[14px] uppercase'></span>
        ) : row?.separator ? (
          <span className='font-bold text-[12px]'>
            {row?.total_gross_amount}
          </span>
        ) : (
          row?.total_gross_amount
        ),
      headerCellClass: '!bg-gray-200'
    },
    {
      header: t('Discount'),
      bodyCellClass: 'border !border-gray-300',
      body: row =>
        row?.isGroupHeader ? (
          <span className='font-bold text-[14px] uppercase'></span>
        ) : row?.separator ? (
          <span className='font-bold text-[12px]'>{row?.total_discount}</span>
        ) : (
          row?.total_discount
        ),
      headerCellClass: '!bg-gray-200'
    },
    {
      header: t('VAT'),
      bodyCellClass: 'border !border-gray-300',
      body: row =>
        row?.isGroupHeader ? (
          <span className='font-bold text-[14px] uppercase'></span>
        ) : row?.separator ? (
          <span className='font-bold text-[12px]'>{row?.total_vat}</span>
        ) : (
          row?.total_vat
        ),
      headerCellClass: '!bg-gray-200'
    },
    {
      header: t('Net with VAT'),
      bodyCellClass: 'border !border-gray-300',
      body: row =>
        row?.isGroupHeader ? (
          <span className='font-bold text-[14px] uppercase'></span>
        ) : row?.separator ? (
          <span className='font-bold text-[12px]'>
            {row?.total_amount_w_vat}
          </span>
        ) : (
          row?.total_amount_w_vat
        ),
      headerCellClass: '!bg-gray-200',
      headerCellStyle: { minWidth: '90px', whiteSpace: 'normal' }
    },
    {
      header: t('Net w/o VAT'),
      bodyCellClass: 'border !border-gray-300',
      body: row =>
        row?.isGroupHeader ? (
          <span className='font-bold text-[14px] uppercase'></span>
        ) : row?.separator ? (
          <span className='font-bold text-[12px]'>
            {row?.total_amount_wo_vat}
          </span>
        ) : (
          row?.total_amount_wo_vat
        ),
      headerCellClass: '!bg-gray-200',
      headerCellStyle: { minWidth: '90px', whiteSpace: 'normal' }
    },
    {
      header: t('Net'),
      bodyCellClass: 'border !border-gray-300',
      body: row =>
        row?.isGroupHeader ? (
          <span className='font-bold text-[14px] uppercase'></span>
        ) : row?.separator ? (
          <span className='font-bold text-[12px]'>{row?.total_net_amount}</span>
        ) : (
          row?.total_net_amount
        ),
      headerCellClass: '!bg-gray-200'
    },
    {
      header: t('Credit (Remaining)'),
      bodyCellClass: 'border !border-gray-300',
      body: row =>
        row?.isGroupHeader ? (
          <span className='font-bold text-[14px] uppercase'></span>
        ) : row?.separator ? (
          <span className='font-bold text-[12px]'>
            {row?.credit_remaining_amount}
          </span>
        ) : (
          row?.credit_remaining_amount
        ),
      headerCellClass: '!bg-gray-200',
      headerCellStyle: { minWidth: '100px', whiteSpace: 'normal' }
    },
    {
      header: t('Payment Method'),
      bodyCellClass: 'border !border-gray-300',
      body: row =>
        row?.isGroupHeader ? (
          <span className='font-bold text-[14px] uppercase'></span>
        ) : row?.separator ? (
          <span className='font-bold text-[12px]'></span>
        ) : (
          row?.payment_method || '-'
        ),
      headerCellClass: '!bg-gray-200'
    }
  ].map(column => ({
    ...column,
    bodyCellStyle: column.bodyCellStyle || detailedCellStyle
  }))

const buildDetailedWorksheet = (data, t) => {
  const formatted = data?.map(row => {
    const isSeparator = row?.separator
    const isGroupHeader = row?.isGroupHeader

    return {
      [t('Date & Time')]: isGroupHeader
        ? ''
        : isSeparator
        ? ''
        : formatDateTime(row?.created_at) || '-',

      [t('Invoice No')]: isGroupHeader
        ? row?.headerValue
        : isSeparator
        ? ''
        : row?.cash_invoice_number || '-',

      [t('MRN')]: isGroupHeader
        ? ''
        : isSeparator
        ? ''
        : row?.patient?.file_no || '-',

      [t('Patient Name')]: isGroupHeader
        ? ''
        : isSeparator
        ? row?.patient_name || t('Subtotal')
        : row?.patient_name || '-',

      [t('Patient Share')]: isGroupHeader
        ? ''
        : isSeparator
        ? row?.group?.patient?.share
        : row?.group?.patient?.share || '0.00',

      [t('Patient VAT')]: isGroupHeader
        ? ''
        : isSeparator
        ? row?.group?.patient?.vat
        : row?.group?.patient?.vat || '0.00',

      [t('Patient Total')]: isGroupHeader
        ? ''
        : isSeparator
        ? row?.group?.patient?.total
        : row?.group?.patient?.total || '0.00',

      [t('Company Name')]: isGroupHeader
        ? ''
        : isSeparator
        ? ''
        : row?.company?.name || '-',

      [t('Company Share')]: isGroupHeader
        ? ''
        : isSeparator
        ? row?.group?.company?.share
        : row?.group?.company?.share || '0.00',

      [t('Company VAT')]: isGroupHeader
        ? ''
        : isSeparator
        ? row?.group?.company?.vat
        : row?.group?.company?.vat || '0.00',

      [t('Company Total')]: isGroupHeader
        ? ''
        : isSeparator
        ? row?.group?.company?.total
        : row?.group?.company?.total || '0.00',

      [t('Gross')]: isGroupHeader
        ? ''
        : isSeparator
        ? row?.total_gross_amount
        : row?.total_gross_amount || '0.00',

      [t('Discount')]: isGroupHeader
        ? ''
        : isSeparator
        ? row?.total_discount
        : row?.total_discount || '0.00',

      [t('VAT')]: isGroupHeader
        ? ''
        : isSeparator
        ? row?.total_vat
        : row?.total_vat || '0.00',

      [t('Net with VAT')]: isGroupHeader
        ? ''
        : isSeparator
        ? row?.total_amount_w_vat
        : row?.total_amount_w_vat || '0.00',

      [t('Net w/o VAT')]: isGroupHeader
        ? ''
        : isSeparator
        ? row?.total_amount_wo_vat
        : row?.total_amount_wo_vat || '0.00',

      [t('Net')]: isGroupHeader
        ? ''
        : isSeparator
        ? row?.total_net_amount
        : row?.total_net_amount || '0.00',

      [t('Credit (Remaining)')]: isGroupHeader
        ? ''
        : isSeparator
        ? row?.credit_remaining_amount
        : row?.credit_remaining_amount || '0.00',

      [t('Payment Method')]: isGroupHeader
        ? ''
        : isSeparator
        ? ''
        : row?.payment_method || '-'
    }
  })

  const worksheet = XLSX.utils.json_to_sheet(formatted)
  const range = XLSX.utils.decode_range(worksheet['!ref'])

  // Styling constants - EXACT same as DoctorWise Report
  const headerStyle = {
    fill: { fgColor: { rgb: 'D1D5DB' } },
    font: { color: { rgb: '111827' }, bold: true, sz: 12 },
    alignment: { horizontal: 'center', vertical: 'center' },
    border: {
      top: { style: 'thin', color: { rgb: '000000' } },
      bottom: { style: 'thin', color: { rgb: '000000' } },
      left: { style: 'thin', color: { rgb: '000000' } },
      right: { style: 'thin', color: { rgb: '000000' } }
    }
  }

  const summaryStyle = {
    fill: { fgColor: { rgb: 'E5E7EB' } },
    font: { bold: true, color: { rgb: '111827' } },
    alignment: { horizontal: 'center', vertical: 'center' },
    border: {
      top: { style: 'thin', color: { rgb: '9CA3AF' } },
      bottom: { style: 'thin', color: { rgb: '9CA3AF' } }
    }
  }

  const zebraStyle = {
    fill: { fgColor: { rgb: 'F1F5F9' } }, // Slate 100
    alignment: { horizontal: 'center', vertical: 'center' }
  }

  const defaultStyle = {
    alignment: { horizontal: 'center', vertical: 'center' }
  }

  // Apply header styles
  for (let C = range.s.c; C <= range.e.c; ++C) {
    const headerAddress = XLSX.utils.encode_col(C) + '1'
    if (worksheet[headerAddress]) {
      worksheet[headerAddress].s = headerStyle
    }

    // Apply row styles based on row type
    for (let R = range.s.r + 1; R <= range.e.r; ++R) {
      const cellAddress = XLSX.utils.encode_cell({ r: R, c: C })
      if (!worksheet[cellAddress]) continue

      const rowData = data[R - 1] // Offset by 1 for header

      if (rowData?.isGroupHeader) {
        worksheet[cellAddress].s = summaryStyle
      } else if (rowData?.separator) {
        worksheet[cellAddress].s = summaryStyle
      } else {
        // Apply Zebra striping to every second data row
        worksheet[cellAddress].s = R % 2 === 0 ? zebraStyle : defaultStyle
      }
    }
  }

  // Adjust column width
  worksheet['!cols'] = new Array(Object.keys(formatted[0] || {}).length).fill({
    wch: 18
  })

  return worksheet
}

export const exportInvoicesToExcel = (
  data,
  t,
  fileName = 'user-collection-detailed-report.xlsx'
) => {
  const workbook = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(
    workbook,
    buildDetailedWorksheet(data, t),
    'User Collection Report'
  )
  XLSX.writeFile(workbook, fileName)
}

export const exportInvoicesIndividualToExcel = (
  blocks,
  combinedRows,
  t,
  fileName = 'user-collection-detailed-report.xlsx'
) => {
  const workbook = XLSX.utils.book_new()
  ;(blocks || []).forEach(block => {
    const sheetName = block.sheetName?.slice?.(0, 31) || 'Individual'
    XLSX.utils.book_append_sheet(
      workbook,
      buildDetailedWorksheet(block.result, t),
      sheetName
    )
  })
  if (combinedRows?.length) {
    XLSX.utils.book_append_sheet(
      workbook,
      buildDetailedWorksheet(combinedRows, t),
      'Combined'
    )
  }
  XLSX.writeFile(workbook, fileName)
}

export const exportInvoicesToPDF = (
  payload,
  t,
  payloadData,
  fileName = 'user-collection-detailed-report.pdf'
) => {
  const doc = new jsPDF('landscape') // 18 columns — landscape is the only way

  doc.addFileToVFS('Amiri-Regular.ttf', AmiriRegBase64)
  doc.addFont('Amiri-Regular.ttf', 'Amiri', 'normal')
  doc.addFileToVFS('Amiri-Bold.ttf', AmiriBoldBase64)
  doc.addFont('Amiri-Bold.ttf', 'Amiri', 'bold')
  doc.setFont('Amiri', 'normal')

  const pageWidth = doc.internal.pageSize.getWidth() // 297mm
  const pageHeight = doc.internal.pageSize.getHeight() // 210mm
  const margin = 5
  const usable = pageWidth - margin * 2 // 287mm
  const currentDate = moment().format('DD/MM/YYYY HH:mm')
  const start_date = moment(payloadData?.start_date).format('DD/MM/YYYY')
  const end_date = moment(payloadData?.end_date).format('DD/MM/YYYY')
  const user_name = payloadData?.user_name || t('All Users')
  const reportTitle = t('User Collection Detailed Report')
  const orgName =
    payloadData?.business_en || payloadData?.business_ar || t('Organization')
  const location = payloadData?.address || ''

  // ── Column widths (total = 287mm) ─────────────────────────────────────────
  const colStyles = {
    0: { cellWidth: 20, halign: 'left' }, // Date
    1: { cellWidth: 30, halign: 'left' }, // Invoice
    2: { cellWidth: 12, halign: 'left' }, // File
    3: { cellWidth: 24, halign: 'left' }, // Patient Name
    4: { cellWidth: 15, halign: 'right' }, // Pt Share
    5: { cellWidth: 15, halign: 'right' }, // Pt VAT
    6: { cellWidth: 15, halign: 'right' }, // Pt Total
    7: { cellWidth: 15, halign: 'right' }, // Co Share
    8: { cellWidth: 15, halign: 'right' }, // Co VAT
    9: { cellWidth: 15, halign: 'right' }, // Co Total
    10: { cellWidth: 14, halign: 'right' }, // Gross
    11: { cellWidth: 14, halign: 'right' }, // Discount
    12: { cellWidth: 14, halign: 'right' }, // VAT
    13: { cellWidth: 14, halign: 'right' }, // Net with VAT
    14: { cellWidth: 14, halign: 'right' }, // Net without VAT
    15: { cellWidth: 14, halign: 'right' }, // Net
    16: { cellWidth: 16, halign: 'right' }, // Credit remaining
    17: { cellWidth: 16, halign: 'left' } // Method
  }
  const drawHeader = () => {
    doc.setFillColor(55, 65, 81)
    doc.rect(0, 0, pageWidth, 24, 'F')
    if (payloadData?.logo) {
      try {
        doc.addImage(
          `data:image/png;base64,${payloadData.logo}`,
          'PNG',
          margin,
          2,
          16,
          16
        )
      } catch (_) {}
    }
    doc.setTextColor(255, 255, 255)
    doc.setFontSize(10)
    doc.setFont('Amiri', 'bold')
    doc.text(orgName, margin + 20, 9)
    doc.setFontSize(7)
    doc.setFont('Amiri', 'normal')
    if (location) doc.text(location, margin + 20, 15)
    doc.setFontSize(11)
    doc.setFont('Amiri', 'bold')
    doc.text(reportTitle, pageWidth - margin, 9, { align: 'right' })
    doc.setFontSize(7)
    doc.setFont('Amiri', 'normal')
    doc.text(`${t('Printed On')}: ${currentDate}`, pageWidth - margin, 15, {
      align: 'right'
    })
  }

  drawHeader()

  let y = 27
  doc.setTextColor(50, 50, 50)
  doc.setFontSize(7)
  doc.setFont('Amiri', 'normal')
  doc.text(`${t('Period')}: ${start_date} - ${end_date}`, margin, y)
  doc.text(`${t('Report For')}: ${user_name}`, pageWidth - margin, y, {
    align: 'right'
  })
  doc.setDrawColor(200, 200, 200)
  doc.setLineWidth(0.3)
  doc.line(margin, y + 2.5, pageWidth - margin, y + 2.5)
  y += 6

  // 18 columns (dropped Company Name — redundant in PDF)
  const tableHeaders = [
    [
      { content: t('Date'), rowSpan: 2 },
      { content: t('Invoice'), rowSpan: 2 },
      { content: t('MRN'), rowSpan: 2 },
      { content: t('Patient'), rowSpan: 2 },
      { content: t('Patient'), colSpan: 3, styles: { halign: 'center' } },
      { content: t('Company'), colSpan: 3, styles: { halign: 'center' } },
      { content: t('Gross'), rowSpan: 2 },
      { content: t('Discount'), rowSpan: 2 },
      { content: t('VAT'), rowSpan: 2 },
      { content: `${t('Net with')}\n${t('VAT')}`, rowSpan: 2 },
      { content: `${t('Net w/o')}\n${t('VAT')}`, rowSpan: 2 },
      { content: t('Net'), rowSpan: 2 },
      { content: `${t('Credit')}\n${t('(Remaining)')}`, rowSpan: 2 },
      { content: t('Method'), rowSpan: 2 }
    ],
    [t('Share'), t('VAT'), t('Total'), t('Share'), t('VAT'), t('Total')]
  ]

  const subtotalStyle = {
    fontStyle: 'bold',
    fillColor: [214, 234, 248],
    textColor: [13, 71, 161],
    fontSize: 8,
    halign: 'right'
  }
  const subtotalLabelStyle = {
    fontStyle: 'bold',
    fillColor: [229, 231, 235],
    textColor: [17, 24, 39],
    fontSize: 8,
    halign: 'left'
  }
  const tableData = []

  payload.forEach(group => {
    // User group header — full width navy bar
    tableData.push([
      {
        content: group?.sectionName
          ? `${group.sectionName} - ${group.header.value}`
          : group.header.value,
        colSpan: 18,
        styles: {
          fontStyle: 'bold',
          fillColor: [209, 213, 219],
          textColor: [17, 24, 39],
          fontSize: 8.5,
          halign: 'left'
        }
      }
    ])

    group.children.forEach(row => {
      const patient = row?.group?.patient || {}
      const company = row?.group?.company || {}
      tableData.push([
        row.created_at ? moment(row.created_at).format('DD/MM HH:mm') : '-',
        row.cash_invoice_number || '-',
        row?.patient?.file_no || '-',
        row?.patient_name || '-',
        patient?.share || '0.00',
        patient?.vat || '0.00',
        patient?.total || '0.00',
        company?.share || '0.00',
        company?.vat || '0.00',
        company?.total || '0.00',
        row?.total_gross_amount || '0.00',
        row?.total_discount || '0.00',
        row?.total_vat || '0.00',
        row?.total_amount_w_vat || '0.00',
        row?.total_amount_wo_vat || '0.00',
        row?.total_net_amount || '0.00',
        row?.credit_remaining_amount || '0.00',
        row?.payment_method || '-'
      ])
    })

    // Subtotal row
    const footer = group?.footer?.value
    tableData.push([
      {
        content: t(group?.footer?.key || 'Subtotal'),
        colSpan: 4,
        styles: subtotalLabelStyle
      },
      {
        content: footer?.group?.patient?.share || '0.00',
        styles: subtotalStyle
      },
      { content: footer?.group?.patient?.vat || '0.00', styles: subtotalStyle },
      {
        content: footer?.group?.patient?.total || '0.00',
        styles: subtotalStyle
      },
      {
        content: footer?.group?.company?.share || '0.00',
        styles: subtotalStyle
      },
      { content: footer?.group?.company?.vat || '0.00', styles: subtotalStyle },
      {
        content: footer?.group?.company?.total || '0.00',
        styles: subtotalStyle
      },
      { content: footer?.total_gross_amount || '0.00', styles: subtotalStyle },
      { content: footer?.total_discount || '0.00', styles: subtotalStyle },
      { content: footer?.total_vat || '0.00', styles: subtotalStyle },
      { content: footer?.total_amount_w_vat || '0.00', styles: subtotalStyle },
      { content: footer?.total_amount_wo_vat || '0.00', styles: subtotalStyle },
      { content: footer?.total_net_amount || '0.00', styles: subtotalStyle },
      {
        content: footer?.credit_remaining_amount || '0.00',
        styles: subtotalStyle
      },
      { content: '', styles: { fillColor: [229, 231, 235] } }
    ])
  })

  autoTable(doc, {
    startY: y,
    head: tableHeaders,
    body: tableData,
    theme: 'grid',
    margin: { left: margin, right: margin, top: 28 },
    tableWidth: usable,
    styles: {
      fontSize: 7,
      cellPadding: { top: 1.8, right: 1.5, bottom: 1.8, left: 1.5 },
      lineColor: [180, 180, 180],
      lineWidth: 0.2,
      font: 'Amiri',
      overflow: 'hidden',
      textColor: [20, 20, 20],
      minCellHeight: 6
    },
    headStyles: {
      fillColor: [209, 213, 219],
      textColor: [17, 24, 39],
      fontStyle: 'bold',
      halign: 'center',
      font: 'Amiri',
      fontSize: 7,
      valign: 'middle',
      minCellHeight: 6
    },
    alternateRowStyles: { fillColor: [248, 249, 252] },
    bodyStyles: {
      fillColor: [255, 255, 255],
      font: 'Amiri',
      fontSize: 7,
      fontStyle: 'normal',
      textColor: [20, 20, 20]
    },
    columnStyles: colStyles,
    showHead: 'everyPage',
    didParseCell: data => {
      if (data.section === 'head' && data.row.index === 0) {
        if (data.column.index === 4) data.cell.colSpan = 3
        if (data.column.index === 7) data.cell.colSpan = 3
      }
    },
    didDrawPage: () => {
      drawHeader()
      const pageCount = doc.internal.getNumberOfPages()
      doc.setFontSize(7)
      doc.setTextColor(130, 130, 130)
      doc.text(
        `${t('Page')} ${doc.internal.getCurrentPageInfo().pageNumber} ${t(
          'of'
        )} ${pageCount}`,
        pageWidth / 2,
        pageHeight - 4,
        { align: 'center' }
      )
    }
  })

  doc.save(fileName)
}

const UserCollectionDetailReport = () => {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const {
    data: requestData,
    executeFetch: requestFetch,
    loading
  } = useFetchData()
  const auth = useSelector(state => state.auth)
  // const [payloadData, setPayloadData] = useState({
  //   end_date: moment().toDate(),
  //   start_date: moment().subtract(1, 'day').toDate(),
  //   created_by: '',
  // })
  const groupedDetailedReports = flattenGroupedDetailedReports(
    requestData?.grouped_reports
  )

  const { filters: payloadData, setFilters: setPayloadData } =
    usePersistedFilters('user_collection_detailed_report', {
      end_date: moment().toDate(),
      start_date: moment().subtract(1, 'day').toDate(),
      created_by: '',
      cash_invoice_number: '',
      department_id: '',
      report_display_mode: REPORT_MODES.individual
    })

  const userBlocks = useMemo(
    () =>
      splitSectionsByUser(requestData?.grouped_reports, {
        getUserKey,
        getUserName
      }).flatMap(section =>
        section.users.map(user => ({
          sheetName: user.label,
          ...flattenUserDetailedReport(section, user)
        }))
      ),
    [requestData]
  )

  const displayMode =
    payloadData?.report_display_mode || REPORT_MODES.combined

  const requestReport = async (payload = payloadData) => {
    await requestFetch('POST', '/v3/reports/user-collection-grouped-detailed', {
      start_date: moment(payload.start_date).format('YYYY-MM-DD'),
      end_date: moment(payload.end_date).format('YYYY-MM-DD'),
      created_by: payload.created_by || ''
    })
  }

  const handleModeChange = event => {
    const value = event?.target?.value ?? event?.value
    setPayloadData(prev => ({ ...prev, report_display_mode: value }))
  }

  const headerGroup = (
    <ColumnGroup>
      <ColumnRow style={{ alignItems: 'end', borderWidth: 1 }}>
        <Column
          header={t('Date & Time')}
          rowSpan={2}
          style={{ paddingLeft: '0.5rem' }}
        />
        <Column
          header={t('Invoice No')}
          rowSpan={2}
          style={{ paddingLeft: '0.5rem' }}
        />
        <Column
          header={t('MRN')}
          rowSpan={2}
          style={{ paddingLeft: '0.5rem' }}
        />

        <Column
          header={t('Name')}
          rowSpan={2}
          style={{ paddingLeft: '0.5rem' }}
        />
        <Column header={t('Patient')} colSpan={3} align={'center'} />

        <Column
          header={t('Name')}
          rowSpan={2}
          style={{ paddingLeft: '0.5rem' }}
        />
        <Column header={t('Company')} colSpan={3} align={'center'} />

        <Column
          header={t('Gross')}
          rowSpan={2}
          style={{ paddingLeft: '0.5rem' }}
        />
        <Column
          header={t('Discount')}
          rowSpan={2}
          style={{ paddingLeft: '0.5rem' }}
        />
        <Column
          header={t('VAT')}
          rowSpan={2}
          style={{ paddingLeft: '0.5rem' }}
        />
        <Column
          header={t('Net with VAT')}
          rowSpan={2}
          style={{
            paddingLeft: '0.5rem',
            minWidth: '90px',
            whiteSpace: 'normal'
          }}
        />
        <Column
          header={t('Net w/o VAT')}
          rowSpan={2}
          style={{
            paddingLeft: '0.5rem',
            minWidth: '90px',
            whiteSpace: 'normal'
          }}
        />
        <Column
          header={t('Net')}
          rowSpan={2}
          style={{ paddingLeft: '0.5rem' }}
        />
        <Column
          header={t('Credit (Remaining)')}
          rowSpan={2}
          style={{
            paddingLeft: '0.5rem',
            minWidth: '100px',
            whiteSpace: 'normal'
          }}
        />
        <Column
          header={t('Payment Method')}
          rowSpan={2}
          style={{ paddingLeft: '0.5rem' }}
        />
      </ColumnRow>
      <ColumnRow>
        <Column header={t('Share')} style={{ paddingLeft: '0.5rem' }} />
        <Column header={t('VAT')} style={{ paddingLeft: '0.5rem' }} />
        <Column header={t('Total')} style={{ paddingLeft: '0.5rem' }} />

        <Column header={t('Share')} style={{ paddingLeft: '0.5rem' }} />
        <Column header={t('VAT')} style={{ paddingLeft: '0.5rem' }} />
        <Column header={t('Total')} style={{ paddingLeft: '0.5rem' }} />
      </ColumnRow>
    </ColumnGroup>
  )

  return (
    <div>
      <div>
        <div className='flex items-center w-full justify-between gap-2 mb-2'>
          <Filters
            payloadData={payloadData}
            setPayloadData={setPayloadData}
            requestReport={requestReport}
            responseReport={() => {}}
            showDepartment={false}
            showCashInvoiceNumber={false}
            maxRangeDays={31}
          />
          <div className='flex gap-2 items-center'>
            <DropdownField
              placeholder={t('Show Reports')}
              className='!mb-0 !w-[180px] !h-[35px] justify-center'
              name='report_display_mode'
              useFormik={false}
              options={reportModeOptions(t)}
              optionLabel='label'
              optionValue='value'
              value={displayMode}
              onChange={handleModeChange}
            />
            <CustomButton
              //label={t('Export to Excel')}
              title={t('Export to Excel')}
              buttonType='success'
              icon={FaFileExcel}
              className='w-fit h-[35px]'
              onClick={() => {
                displayMode === REPORT_MODES.combined
                  ? exportInvoicesToExcel(groupedDetailedReports.result, t)
                  : exportInvoicesIndividualToExcel(
                      userBlocks,
                      displayMode === REPORT_MODES.both
                        ? groupedDetailedReports.result
                        : [],
                      t
                    )
              }}
            />

            <CustomButton
              //label={t('Export to PDF')}
              title={t('Export to PDF')}
              buttonType='danger'
              icon={FaFilePdf}
              className='w-fit h-[35px]'
              onClick={() => {
                exportInvoicesToPDF(
                  displayMode === REPORT_MODES.combined
                    ? groupedDetailedReports.pdf
                    : [
                        ...userBlocks.flatMap(block => block.pdf),
                        ...(displayMode === REPORT_MODES.both
                          ? groupedDetailedReports.pdf
                          : [])
                      ],
                  t,
                  {
                    ...payloadData,
                    business_en: auth?.buisnessInfo?.name_en,
                    business_ar: auth?.buisnessInfo?.name_ar,
                    address: auth?.buisnessInfo?.address,
                    logo: auth?.buisnessInfo?.logo
                  }
                )
              }}
            />
          </div>
        </div>

        {displayMode === REPORT_MODES.combined ? (
          <Table
            data={groupedDetailedReports.result}
            loading={loading}
            columns={columns}
            headerColumnGroup={headerGroup}
            showGridlines
            rowClassName={detailedRowClassName}
            rootyStyles='overflow-auto border border-gray-200 rounded border-b-0 custom-table user-collection-detail-table'
          />
        ) : (
          <div className='space-y-4'>
            {userBlocks.map((block, index) => (
              <div key={index}>
                <div className='bg-gray-300 text-gray-800 font-bold text-[14px] uppercase tracking-wide px-4 py-2 rounded-t'>
                  {block.sheetName}
                </div>
                <Table
                  data={block.result}
                  loading={loading}
                  columns={columns}
                  headerColumnGroup={headerGroup}
                  showGridlines
                  rowClassName={detailedRowClassName}
                  rootyStyles='overflow-auto border border-gray-200 rounded border-b-0 custom-table user-collection-detail-table'
                />
              </div>
            ))}
            {displayMode === REPORT_MODES.both &&
              groupedDetailedReports.result?.length > 0 && (
                <div>
                  <div className='bg-gray-300 text-gray-800 font-bold text-[14px] uppercase tracking-wide px-4 py-2 rounded-t'>
                    {t('Combined')}
                  </div>
                  <Table
                    data={groupedDetailedReports.result}
                    loading={loading}
                    columns={columns}
                    headerColumnGroup={headerGroup}
                    showGridlines
                    rowClassName={detailedRowClassName}
                    rootyStyles='overflow-auto border border-gray-200 rounded border-b-0 custom-table user-collection-detail-table'
                  />
                </div>
              )}
          </div>
        )}
      </div>
    </div>
  )
}

export default UserCollectionDetailReport
